import CustomMetaData, {
  CustomMetaDataProps,
} from 'components/general/customMetaData/CustomMetaData';
import { gsap } from 'gsap/dist/gsap';
import { NEXTJS_REVALIDATE_PAGE_IN_SECONDS } from 'pages/index';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { getSharedData } from 'utils/parsers';
import style from './indexWorkItem.module.scss';

import { ScrollToPlugin } from 'gsap/dist/ScrollToPlugin';
import { SharedDataType, useGlobalContentContext } from 'context/GlobalContentContext';
import { WorkIntroText } from 'components/workIntroText/WorkIntroText';
import { StandardBlocksLayout } from 'components/general/blocks/standardBlocksLayout/StandardBlocksLayout';
import {
  BlockType,
  DefaultMediaBlockProps,
} from 'components/general/blocks/switchBlock/SwitchBlock';
import { ImageBlock } from 'components/general/blocks/imageBlock/ImageBlock';
import Link from 'next/link';
import { getEndpoint, getCSSVar } from 'utils/helpers';
import { useRouter } from 'next/router';
import { MainFooter } from 'components/mainFooter/MainFooter';
import { MobileProjectCarousel } from 'components/mobileProjectCarousel/MobileProjectCarousel';
import LogoMobile from 'components/logo/logo-mobile.svg';

if (typeof window !== 'undefined') {
  // Due to SSR we can only register when we are in the browser
  gsap.registerPlugin(ScrollToPlugin);
}

export type ProjectType = {
  slug: string;
  // API url
  url: string;
  title: string;
  intro: string;
  is_published: boolean;
  hero: DefaultMediaBlockProps;
  client: { name: string };
  blocks: BlockType[];
  meta_title: string;
  meta_description: string;
  meta_image: string;
  sidebar_heading: string;
};

type ProjectNotFoundType = {
  detail: string;
};

export type ImageType = {
  image: string;
  image_width: number;
  image_height: number;
  alt_text: string;
};

type Props = { globalData: SharedDataType; project: ProjectType } & {
  metaData: CustomMetaDataProps;
};

const Project = ({ project, metaData, globalData }: Props) => {
  const router = useRouter();

  const [isTouch, setIsTouch] = useState<boolean>(false);

  const intro = useRef<HTMLDivElement>(null);
  const blocks = useRef<HTMLDivElement>(null);

  const onTouchStart = () => {
    if (!isTouch) setIsTouch(true);
  };

  const { setData } = useGlobalContentContext();
  useEffect(() => {
    const copy = { ...globalData };
    copy.sidebar.title = project.sidebar_heading;
    setData(copy);
  }, [project.sidebar_heading]);

  useEffect(() => {
    window.addEventListener('touchstart', onTouchStart);
    return () => {
      window.removeEventListener('touchstart', onTouchStart);
    };
  }, []);

  useEffect(() => {
    if (!router.pathname.includes('/work/')) {
      gsap.set([intro.current], { autoAlpha: 1 });
      gsap.set(['.js-first-block'], { autoAlpha: 1 });
      return;
    }

    // intro animation, mayeb do this with just CSS so we can change it on media queries?
    gsap.set([intro.current], { autoAlpha: 0, y: 200 });
    gsap.set(['.js-first-block'], { autoAlpha: 0, y: 200 });
    // delay by --pageTransitionDelay
    const delayMS = getCSSVar('--pageTransitionDelay');
    const introDuration = getCSSVar('--projectIntroTime') / 1000;
    // we need a delay here because otherwise project>project ends up crossfading
    const baseDelay = delayMS / 1000;
    gsap.to(intro.current, { y: 0, autoAlpha: 1, duration: introDuration, delay: baseDelay });
    gsap.to('.js-first-block', {
      y: 0,
      autoAlpha: 1,
      duration: introDuration,
      delay: baseDelay + 0.2,
    });
  }, [router.pathname]);

  const tabletState = isTouch ? style.emptySpaceForCarouselTablet : null;

  const ForwardedLogo = forwardRef((children, ref) => {
    return <LogoMobile />;
  });
  ForwardedLogo.displayName = 'ForwardedLogo';
  return (
    <div className={style.container}>
      <CustomMetaData {...metaData} />

      <div className={style.content}>
        <div className={style.grid}>
          <div className={style.projectHeroPositionHolder}>
            <div className={style.projectHeroSizer}>
              {!project.is_published && (
                <ImageBlock
                  desktop={{
                    src: project.hero.image,
                    layout: 'fill',
                    objectFit: 'cover',
                    loading: 'eager',
                    sizes: '35vw',
                  }}
                  alt={project.hero.alt_text}
                />
              )}
            </div>
            {project.hero.caption && (
              <p className={style.heroCaption}>{project.hero.caption}</p>
            )}
          </div>
          <div ref={intro} className={style.intro}>
            <WorkIntroText
              client={project.client.name}
              title={project.title}
              html={project.intro}
              hero={project.hero}
            />
          </div>
          <div ref={blocks} className={style.dynamicBlocks}>
            <StandardBlocksLayout blocks={project.blocks} blockStyles={style.blocks} />
          </div>
        </div>
      </div>
      <div className={`${style.emptySpaceForCarousel} ${tabletState} js-work-scrolltrigger`} />
      <div className={`${style.mobileCarouselFooter} js-mobile-projects-carousel`}>
        <div className={style.mobileLogoGrid}>
          <div className={style.mobileLogo}>
            <Link href="/" passHref>
              <ForwardedLogo />
            </Link>
          </div>
          {/* <Logo large={true} /> */}
        </div>
        <MobileProjectCarousel currentProjectSlug={project.slug} projects={globalData.projects} />
        <MainFooter showMenu={false} showSocial={true} reduceHeight={true} />
      </div>
    </div>
  );
};

export default Project;

// This function gets called at build time
export async function getStaticPaths() {
  const projects = await getEndpoint('/projects/');

  // Get the paths we want to pre-render based on posts
  const paths = projects.map((post, index) => {
    return {
      params: { slug: post.slug },
    };
  });

  // We'll pre-render only these paths at build time.
  // { fallback: blocking } will server-render pages
  // on-demand if the path doesn't exist.
  return { paths, fallback: 'blocking' };
}

// This also gets called at build time
export async function getStaticProps(context) {
  const { params } = context;

  const project = await getEndpoint(`/projects/${params.slug}/`, context);

  // 404 page
  if (Object.keys(project).length === 0) {
    return {
      notFound: true,
      revalidate: 10,
    };
  }

  const globalData = await getSharedData(context);

  // Pass project data to the page via props
  globalData.sidebar = { title: project.sidebar_heading };

  const props: Props = {
    project,
    globalData,
    metaData: {
      title: project.meta_title,
      description: project.meta_description,
      image: project.meta_image,
    },
  };
  return {
    props: props,
    // Next.js will attempt to re-generate the page:
    // - When a request comes in
    // - At most once every 10 seconds
    revalidate: NEXTJS_REVALIDATE_PAGE_IN_SECONDS, // In seconds
  };
}
